import * as React from 'react';
import * as GoogleAnalytics from 'react-ga';
import { RouteComponentProps } from 'react-router';

GoogleAnalytics.initialize('UA-124589575-1', {
  debug: true,
  titleCase: false
});

const ga = GoogleAnalytics.ga();
if (typeof ga === 'function') {
  ga('send', 'screenview', {
    appName: process.env.REACT_APP_NAME,
    appVersion: process.env.REACT_APP_VERSION,
    screenName: 'web',
  });
}

/**
 * Send JavaScript error information to Google Analytics.
 *
 * @param  {Window} window A reference to the "window".
 * @return {void}
 * @author Philippe Sawicki <https://github.com/philsawicki>
 */
((window) => {
  // Retain a reference to the previous global error handler, in case it has been set:
  const originalWindowErrorCallback = window.onerror;

  /**
   * Log any script error to Google Analytics.
   *
   * Third-party scripts without CORS will only provide "Script Error." as an error message.
   *
   * @param  {String}           errorMessage Error message.
   * @param  {String}           url          URL where error was raised.
   * @param  {Number}           lineNumber   Line number where error was raised.
   * @param  {Number|undefined} columnNumber Column number for the line where the error occurred.
   * @param  {Object|undefined} errorObject  Error Object.
   * @return {Boolean}                       When the function returns true, this prevents the
   *                                         firing of the default event handler.
   */
  window.onerror = (errorMessage, url, lineNumber, columnNumber, errorObject) => {
    const myGa = GoogleAnalytics.ga();
    if (typeof myGa === 'function') {
      // In case the "errorObject" is available, use its data, else fallback
      // on the default "errorMessage" provided:
      let exceptionDescription = errorMessage;
      if (typeof errorObject !== 'undefined' && typeof errorObject.message !== 'undefined') {
        exceptionDescription = errorObject.message;
      }

      // Format the message to log to Analytics (might also use "errorObject.stack" if defined):
      exceptionDescription += ' at ' + url + ':' + lineNumber + ':' + columnNumber;

      myGa('send', 'exception', {
        exDescription: exceptionDescription,
        exFatal: false, // Some Error types might be considered as fatal.
      });
    }

    // If the previous "window.onerror" callback can be called, pass it the data:
    if (typeof originalWindowErrorCallback === 'function') {
      return originalWindowErrorCallback(errorMessage, url, lineNumber, columnNumber, errorObject);
    }
    // Otherwise, Let the default handler run:
    return false;
  };
})(window);

interface IWithTrackerOptions {
  gaOptions?: {};
  props?: {};
}

export function gaEvent(category: string, action: string, value?: number, label?: string) {
  const myGa = GoogleAnalytics.ga();
  if (typeof myGa === 'function') {
    myGa('send', 'event', category, action, label, value, {
      appName: process.env.REACT_APP_NAME,
      appVersion: process.env.REACT_APP_VERSION,
    });
  }
}

const withTracker = (WrappedComponent: React.ComponentType, options: IWithTrackerOptions = {}) => {
  const trackPage = (page: string) => {
    const myGa = GoogleAnalytics.ga();
    if (typeof myGa === 'function') {
      myGa('set', {
        appName: process.env.REACT_APP_NAME,
        appVersion: process.env.REACT_APP_VERSION,
        page,
      });
      myGa('send', 'pageview');
    }
  };

  return class extends React.Component<RouteComponentProps<{}>> {
    public componentDidMount() {
      const page = this.props.location.pathname;
      trackPage(page);

      const script = document.createElement("script");
      script.src = "https://c.leadlab.click/f037495da9f85046.js";
      script.async = true;
      // script.onload = () => this.scriptLoaded();

      document.body.appendChild(script);
    }

    public componentWillReceiveProps(nextProps: RouteComponentProps<{}>) {
      const currentPage = this.props.location.pathname;
      const nextPage = nextProps.location.pathname;

      if (currentPage !== nextPage) {
        trackPage(nextPage);
      }
    }

    public render() {
      return <WrappedComponent {...this.props} {...options.props} />;
    }
  };
};

export default withTracker;



// WEBPACK FOOTER //
// ./src/with_tracker.tsx